/* COURIER SHIPPING APPROVAL — Tina Lee, mail "Freight on Helinox Carrier Accounts_Feasibility Review_20260811",
   built on Max's word 2026-10-19. Two screens for two roles, the split the portal keeps everywhere else (Project
   Tracker / Project Approval, GTM Programmes / GTM Approval):

   * /courier — anybody about to ship on a Helinox courier account writes the request here, before dispatch, and
     follows it. The form is Young's six lines (standing rules of 2026-08-06) plus what his rules 2, 5 and 6 ask
     for, and the check beside it is the server's own (`assess` in courier.js) — the same function the submit
     stores, so the preview cannot promise what the stored check does not say.
   * /courier-approval — Tina's queue: what waits, what she decided, and the rates the check is measured against.

   Tina's draft had a password-protected admin page; here the approver is a person named in portal settings
   (courier.approver) and signs in like everybody else. */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, X, CheckCircle2, AlertTriangle, OctagonAlert, Info, Eye, Pencil, Send, Copy, Trash2, Compass } from 'lucide-react';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import { DecisionDialog } from './DecisionDialog';
import { BACKEND_URL } from './backendConfig';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import useFrozenColumns from './useFrozenColumns';
import TourGuide from './TourGuide';
import ListPicker from './ListPicker';
import { fmtDateTimeVN } from './timeZone';
import './SPHub.css';
import './ProjectApproval.css';
import './Courier.css';

const API = `${BACKEND_URL}/api/courier`;
const get = async (url) => {
  const r = await fetch(url, { credentials: 'include' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { errors: j.errors });
  return j;
};
const send = async (url, method, body) => {
  const r = await fetch(url, {
    method, credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { errors: j.errors });
  return j;
};
const dash = '—';
const n2 = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? dash
  : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const money = (v, c) => (v === null || v === undefined || v === '' ? dash : `${c || ''} ${n2(v)}`.trim());
const carrierOf = (r) => (r.carrier === 'Other' ? (r.carrierOther || 'Other') : r.carrier);

const EMPTY = {
  department: '', carrier: 'FedEx', carrierOther: '', service: '', accountHolder: '', accountNo: '',
  shipperName: '', originCountry: '', originCity: '', originAddress: '',
  receiverName: '', destCountry: '', destCity: '', destAddress: '',
  toIndividual: false, importerOfRecord: '', shipmentTo: 'internal', freightPayer: '', incoterm: '',
  cartons: '', actualKg: '', dimKg: '', estCost: '', estSurcharge: '', estCurrency: '', estDuties: '',
  fwdName: '', fwdCost: '', fwdCurrency: '', goodsFor: '', purpose: '', contents: '', notes: '', ccEmails: '',
};
const INCOTERMS = ['EXW', 'FCA', 'FOB', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];

/* The four colours of a check, the same four words everywhere it is drawn. */
const LEVEL_ICON = { ok: CheckCircle2, info: Info, warn: AlertTriangle, stop: OctagonAlert };
export const VerdictChip = ({ v }) => {
  const { t } = useTranslation();
  if (!v) return <span className="cr-verdict none">{dash}</span>;
  return <span className={`cr-verdict ${v}`}>{t(`cr.v_${v}`)}</span>;
};
const StatusChip = ({ s }) => {
  const { t } = useTranslation();
  return <span className={`cr-status ${s}`}>{t(`cr.s_${s}`)}</span>;
};

/* What the check found. Drawn the same in the form's preview and on a stored request. */
/* Before the form holds enough to price, the panel still says what it IS and what it is waiting for (Max,
   2026-10-19, on the empty box: "chỗ này là sao ko hiểu") — a sentence about filling in fields read as an error. */
const CheckPanel = ({ a, busy, missing, remark }) => {
  const { t } = useTranslation();
  if (!a) return (
    <div className="cr-check empty" data-testid="cr-check-empty">
      <div className="cr-check-head">
        <span className="cr-check-title">{t('cr.checkTitle')}</span>
        {busy && <Loader2 size={14} className="pa-spin" />}
      </div>
      <p className="cr-check-what">{t('cr.checkWhat')}</p>
      {missing && missing.length > 0 && <p className="cr-check-need">{t('cr.checkNeed', { list: missing.join(', ') })}</p>}
    </div>
  );
  /* READ IN THREE STEPS, not one block (Max, 2026-10-19: "nội dung cứ dồn lại 1 cục á khó đọc"): the answer first —
     the estimate against the contract price and how far apart they are — then the shipment and where the figures
     came from as label / value rows, then what the check found, one line each. And it says what it IS: a calculation
     on Tina's contract rate file, not an AI review (Max asked whether it was AI, and an unlabelled judgement on a
     screen invites that question). */
  const Row = ({ k, children }) => <><dt>{k}</dt><dd>{children}</dd></>;
  const diffCls = a.diffPct === null || a.diffPct === undefined ? '' : a.verdict || '';
  return (
    <div className="cr-check" data-testid="cr-check">
      <div className="cr-check-head">
        <span className="cr-check-title">{t('cr.checkTitle')}</span>
        <VerdictChip v={a.verdict} />
        {busy && <Loader2 size={14} className="pa-spin" />}
      </div>
      <div className="cr-check-src">{t('cr.checkSource')}</div>
      <div className="cr-cmp">
        {/* HPAC's DHL contract (29/10) is in SGD: such a check carries its own currency and amounts (currency, quoted,
            contract, contractWithFuel); a FedEx one keeps the KRW fields it always had. */}
        <div><span>{t('cr.quoted')}</span><b>{a.currency ? (a.quoted !== null && a.quoted !== undefined ? `${a.currency} ${n2(a.quoted)}` : dash)
          : a.quotedKRW !== null ? `KRW ${n2(Math.round(a.quotedKRW))}` : dash}</b>
          {a.quotedUSD !== null && <small>USD {n2(a.quotedUSD)}</small>}</div>
        {/* What the contract figure IS, under it: fuel only where there is some (Tina, 2026-10-20: the contract has
            none), else whether it is the file's Economy rate or Priority at its factor. A check stored before 20/10
            carries no serviceFactor and says nothing here. */}
        <div><span>{t('cr.contract')}</span><b>{a.contractKRW ? `KRW ${n2(a.contractWithFuelKRW)}` : a.contract ? `${a.currency} ${n2(a.contractWithFuel)}` : dash}</b>
          {a.contract && !a.contractKRW ? <small>{a.fuelPct ? t('cr.withFuel', { pct: n2(a.fuelPct) }) : t('cr.dhlFreightOnly')}</small>
          : !a.contractKRW ? <small>{t('cr.noContract')}</small>
            : a.fuelPct ? <small>{t('cr.withFuel', { pct: n2(a.fuelPct) })}</small>
              : a.serviceFactor > 1 ? <small>{t('cr.prioRate', { f: a.serviceFactor })}</small>
                : a.serviceFactor === 1 ? <small>{t('cr.econRate')}</small> : null}</div>
        {a.diffPct !== null && a.diffPct !== undefined && (
          <div className={`cr-diff ${diffCls}`}><span>{t('cr.diff')}</span><b>{a.diffPct > 0 ? '+' : ''}{a.diffPct}%</b></div>
        )}
      </div>
      <div className="cr-check-sec">{t('cr.h_shipment')}</div>
      <dl className="cr-rows">
        <Row k={t('cr.chargeable')}>{n2(a.chargeableKg)} kg{a.bulky ? <span className="cr-bulky">{t('cr.bulky')}</span> : null}</Row>
        {a.billedKg ? <Row k={t('cr.billed')}>{n2(a.billedKg)} kg</Row> : null}
        {a.direction && <Row k={t('cr.lane')}>{t(`cr.dir_${a.direction}`)}{a.lane ? ` · ${a.lane}` : ''}</Row>}
        {a.economyKRW && a.serviceFactor > 1 ? <Row k={t('cr.economyBase')}>KRW {n2(a.economyKRW)}</Row> : null}
        {a.contractKRW && a.contractWithFuelKRW !== a.contractKRW ? <Row k={t('cr.contractBase')}>KRW {n2(a.contractKRW)}</Row> : null}
        {a.contract && a.contractWithFuel !== a.contract ? <Row k={t('cr.contractBase')}>{a.currency} {n2(a.contract)}</Row> : null}
        {a.usdPerKg !== null && <Row k={t('cr.usdPerKg')}>USD {n2(a.usdPerKg)}</Row>}
      </dl>
      {/* "Where the figures come from" (the FX week and the rate file's name) came off (Max, 2026-10-19: "bỏ cái này
          nhé"); the source line under the title already says the price is the contract rate file. */}
      {(a.checks || []).length > 0 && <>
        <div className="cr-check-sec">{t('cr.h_findings')}</div>
        <ul className="cr-checks">
          {(a.checks || []).map((c, i) => {
            const Icon = LEVEL_ICON[c.level] || Info;
            return <li key={`${c.code}-${i}`} className={`cr-lv ${c.level}`}><Icon size={15} /> <span>{c.text}</span></li>;
          })}
        </ul>
      </>}
      {/* Tina, 2026-10-20: the rate file cannot answer the other charges FedEx may add, so every contract comparison
          says so — her words, from the Rates tab. Only beside a contract price: on a DHL or by-hand check it would
          qualify a figure that is not there. */}
      {(a.contractKRW || a.contract) && remark ? <>
        <div className="cr-check-sec">{t('cr.h_remark')}</div>
        <p className="cr-remark" data-testid="cr-remark">{remark}</p>
      </> : null}
    </div>
  );
};

/* ---- the request form ------------------------------------------------------------------------ */
const Field = ({ label, req, hint, children, wide, tip }) => (
  <label className={`cr-field${wide ? ' wide' : ''}`}>
    <span className="cr-label" title={tip || undefined}>{label}{req ? <b className="cr-req"> *</b> : null}</span>
    {children}
    {hint ? <span className="cr-hint">{hint}</span> : null}
  </label>
);

/* A MONEY BOX THAT SHOWS ITS THOUSANDS (Max, 2026-10-19: "thể hiện dấu phẩy để phân cách hàng nghìn nhé"). The form
   keeps the plain number ("646465555"), so what is sent, checked and stored is unchanged; only the box draws
   "646,465,555". A text box, not type=number — a number box cannot show a comma — and the caret keeps its place among
   the DIGITS while the commas come and go around it. */
const groupThousands = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const [int, dec] = String(raw).split('.');
  const g = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec !== undefined ? `${g}.${dec}` : g;
};
const MoneyInput = ({ value, onChange, ...rest }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const n = caret.current;
    if (!el || n === null || document.activeElement !== el) return;
    caret.current = null;
    let seen = 0;
    let pos = 0;
    while (pos < el.value.length && seen < n) { if (/[\d.]/.test(el.value[pos])) seen += 1; pos += 1; }
    el.setSelectionRange(pos, pos);
  });
  const change = (e) => {
    const el = e.target;
    caret.current = el.value.slice(0, el.selectionStart ?? el.value.length).replace(/[^\d.]/g, '').length;
    let raw = el.value.replace(/[^\d.]/g, '');
    const dot = raw.indexOf('.');
    if (dot >= 0) raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    onChange(raw.replace(/^0+(?=\d)/, ''));
  };
  return <input ref={ref} type="text" inputMode="decimal" autoComplete="off" value={groupThousands(value)} onChange={change} {...rest} />;
};

/* A stored request as the form's values: the form's own keys, a number as the text a box holds. */
const fromRow = (r) => {
  const out = { ...EMPTY };
  Object.keys(EMPTY).forEach((k) => {
    const v = r[k];
    if (v === null || v === undefined) return;
    out[k] = typeof EMPTY[k] === 'boolean' ? !!v : (typeof v === 'number' ? String(v) : v);
  });
  return out;
};

/* ONE FORM FOR A REQUEST, four ways (Max, 2026-10-19): NEW; EDIT one of one's own (a draft, or one turned down or
   withdrawn); a COPY of one (Duplicate — a new request, nothing saved until Save draft or Send); and VIEW, the
   request READ as the form it was sent as, every field locked ("hiện giống form đã điền á"), with its stored check and
   what may be done with it beside. Save draft keeps a half-filled form; Send checks everything. */
const RequestForm = ({ meta, onDone, onCancel, mode = 'new', r = null, extraTop = null, sideActions = null }) => {
  const { t } = useTranslation();
  const view = mode === 'view';
  const waiting = mode === 'edit' && r && r.status === 'waiting';
  /* The requester's own main department, ready to change — or what the request already holds. */
  const [f, setF] = useState(() => (r ? fromRow(r) : { ...EMPTY, department: meta.me.department || '' }));
  const [deps, setDeps] = useState(meta.departments || []);
  const [depNote, setDepNote] = useState('');
  const [box, setBox] = useState({ l: '', w: '', h: '' });
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const seq = useRef(0);
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setF((p) => ({ ...p, [k]: v }));
  };
  const services = (meta.services || {})[f.carrier] || [];
  const serviceGroups = (meta.serviceGroups || {})[f.carrier] || [];
  const grouped = new Set(serviceGroups.flatMap((g) => g.items.map(([v]) => v)));
  const actual = Number(f.actualKg) || 0;
  const dim = Number(f.dimKg) || 0;
  /* Young's rule 2, said where it is decided: the box is drawn the moment the shipment is bulky. */
  const bulky = (dim > 0 && dim > actual) || actual > (meta.bulkyKg || 100);

  /* The live check: the server's own `assess`, asked again a moment after the typing stops, and an
     answer overtaken by a newer question is dropped. */
  useEffect(() => {
    if (view || !f.carrier || !(Number(f.actualKg) > 0) || !(Number(f.estCost) > 0) || !f.estCurrency || !f.destCountry) {
      setCheck(null);
      return undefined;
    }
    const my = ++seq.current;
    setChecking(true);
    const tm = setTimeout(() => {
      send(`${API}/assess`, 'POST', f)
        .then((j) => { if (my === seq.current) setCheck(j.assessment); })
        .catch(() => { if (my === seq.current) setCheck(null); })
        .finally(() => { if (my === seq.current) setChecking(false); });
    }, 450);
    return () => clearTimeout(tm);
  }, [f, view]);

  /* Tina's own helper: the dimensional weight of one carton is L × W × H ÷ 5,000, times the cartons. */
  const fromBox = () => {
    const l = Number(box.l); const w = Number(box.w); const h = Number(box.h);
    const c = Number(f.cartons) || 1;
    if (l > 0 && w > 0 && h > 0) setF((p) => ({ ...p, dimKg: String(Math.round(((l * w * h) / 5000) * c * 100) / 100) }));
  };

  /* Department: picked from the shared list, or added to it from the list's own "+ Add" row — the picker the
     Sales Rep field uses (Max: "department cho dropdown chọn và add giá trị mới", then "nên làm để +add… như cái
     tui chụp á"). A name differing only by case or spacing is offered as the one already there, by the picker. */
  const addDep = async (name) => {
    setF((p) => ({ ...p, department: name }));
    try {
      const j = await send(`${API}/departments`, 'POST', { name });
      setDeps(j.departments || deps);
      setF((p) => ({ ...p, department: j.name }));
      setDepNote(j.added ? t('cr.depAdded', { name: j.name }) : '');
    } catch (e) { setDepNote(e.message); }
  };

  const submit = async (draft) => {
    setBusy(true); setErrors([]);
    try {
      const body = { ...f, draft };
      const j = mode === 'edit'
        ? await send(`${API}/requests/${encodeURIComponent(r.no)}`, 'PUT', body)
        : await send(`${API}/requests`, 'POST', body);
      onDone(j.request, j.mailed, draft, !!j.byApprover);
    } catch (e) {
      setErrors(e.errors && e.errors.length ? e.errors : [e.message]);
    } finally { setBusy(false); }
  };
  const countries = meta.countries || [];
  const entities = meta.entities || [];

  return (
    <div className={`pa-section ${view ? 'cr-detail' : 'cr-form'}`} data-testid={view ? 'cr-detail' : 'cr-form'}>
      <div className="cr-form-head">
        <h3 className="pa-sec-title">
          {view ? <><span className="mono">{r.no}</span> <StatusChip s={r.status} /></>
            : mode === 'edit' ? t('cr.editTitle', { no: r.no })
              : mode === 'copy' ? t('cr.copyTitle', { no: r.no }) : t('cr.newTitle')}
        </h3>
        <button type="button" className="cr-x" onClick={onCancel} title={t('cr.close')} aria-label={t('cr.close')}><X size={18} /></button>
      </div>
      {view && (
        <p className="pa-sec-note">{r.submittedAt ? t('cr.raised', { at: fmtDateTimeVN(r.submittedAt), by: r.createdBy || dash })
          : `${t('cr.notSent')} · ${r.createdBy || dash}`}
          {r.updatedBy ? ` ${t('cr.changedBy', { by: r.updatedBy, at: fmtDateTimeVN(r.updatedAt) })}` : null}</p>
      )}
      <div className="cr-form-grid">
        <div className="cr-form-main">
          {extraTop}
          <fieldset className={`cr-fields${view ? ' cr-ro' : ''}`} disabled={view}>
          <section className="cr-block cr-b-req"><div className="cr-hd">{t('cr.b_requester')}</div><div className="cr-bd">
            <div className="cr-row">
              <Field label={t('cr.requester')}><input value={r && mode !== 'copy'
                ? `${r.createdBy || ''}${r.requesterEntity ? ` · ${r.requesterEntity}` : ''}`
                : `${meta.me.name}${meta.me.entity ? ` · ${meta.me.entity}` : ''}`} readOnly /></Field>
              <Field label={t('cr.department')} hint={depNote}>
                <ListPicker value={f.department} options={deps} placeholder={t('cr.depPick')}
                  onChange={(v) => { setDepNote(''); setF((p) => ({ ...p, department: v })); }}
                  onAdd={(n) => { setDepNote(''); addDep(n); }}
                  emptyLabel={(n) => t('cr.depNone', { name: n })}
                  addLabel={(n) => t('cr.depAddTo', { name: n })} />
              </Field>
              <Field label={t('cr.ccEmails')} hint={t('cr.ccHint')} wide>
                <input name="ccEmails" value={f.ccEmails} onChange={set('ccEmails')} placeholder="name@helinox.com, …" />
              </Field>
            </div>
          </div></section>

          <section className="cr-block cr-b-car"><div className="cr-hd">{t('cr.b_carrier')}</div><div className="cr-bd">
            <div className="cr-row">
              <Field label={t('cr.carrier')} req>
                <select name="carrier" value={f.carrier} onChange={(e) => setF((p) => ({ ...p, carrier: e.target.value, service: '' }))}>
                  {(meta.carriers || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              {f.carrier === 'Other' && <Field label={t('cr.carrierOther')} req><input name="carrierOther" value={f.carrierOther} onChange={set('carrierOther')} maxLength={60} /></Field>}
              <Field label={t('cr.service')} req>
                {services.length
                  ? <select name="service" value={f.service} onChange={set('service')}>
                      <option value="">{t('cr.pick')}</option>
                      {/* Grouped and worded as Tina's list where the server sends groups (FedEx); the value is still
                          the full service name. A service the groups do not name is listed after them. */}
                      {serviceGroups.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.items.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                        </optgroup>
                      ))}
                      {services.filter((s) => !grouped.has(s)).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  : <input name="service" value={f.service} onChange={set('service')} maxLength={120} />}
              </Field>
              <Field label={t('cr.accountHolder')} req>
                <select name="accountHolder" value={f.accountHolder} onChange={set('accountHolder')} title={t('cr.accountHint')}>
                  <option value="">{t('cr.pick')}</option>
                  {entities.map((e) => <option key={e.key} value={e.key}>{e.key} — {e.name}</option>)}
                </select>
              </Field>
              <Field label={t('cr.accountNo')} req><input name="accountNo" value={f.accountNo} onChange={set('accountNo')} maxLength={60} /></Field>
            </div>
          </div></section>

          <section className="cr-block cr-b-route"><div className="cr-hd">{t('cr.b_route')}</div><div className="cr-bd">
            <div className="cr-route">
              <div>
                <div className="cr-sub">{t('cr.from')}</div>
                <Field label={t('cr.shipper')} req><input name="shipperName" value={f.shipperName} onChange={set('shipperName')} maxLength={160} /></Field>
                <div className="cr-row">
                  <Field label={t('cr.country')} req>
                    <select name="originCountry" value={f.originCountry} onChange={set('originCountry')}>
                      <option value="">{t('cr.pick')}</option>
                      {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label={t('cr.city')} req><input name="originCity" value={f.originCity} onChange={set('originCity')} maxLength={120} /></Field>
                </div>
                <Field label={t('cr.address')} req hint={t('cr.zipHint')}><textarea name="originAddress" rows={2} value={f.originAddress} onChange={set('originAddress')} maxLength={500} /></Field>
              </div>
              <div>
                <div className="cr-sub">{t('cr.to')}</div>
                <Field label={t('cr.receiver')} req><input name="receiverName" value={f.receiverName} onChange={set('receiverName')} maxLength={160} /></Field>
                <div className="cr-row">
                  <Field label={t('cr.country')} req>
                    <select name="destCountry" value={f.destCountry} onChange={set('destCountry')}>
                      <option value="">{t('cr.pick')}</option>
                      {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label={t('cr.city')} req><input name="destCity" value={f.destCity} onChange={set('destCity')} maxLength={120} /></Field>
                </div>
                <Field label={t('cr.address')} req hint={t('cr.zipHint')}><textarea name="destAddress" rows={2} value={f.destAddress} onChange={set('destAddress')} maxLength={500} /></Field>
              </div>
            </div>
            {/* Young's rule 1, sixth line. It sits with the route because it says where the goods end up. */}
            <Field label={t('cr.goodsFor')} req wide>
              <input name="goodsFor" value={f.goodsFor} onChange={set('goodsFor')} maxLength={200} placeholder={t('cr.goodsForHint')} />
            </Field>
            <div className="cr-row">
              <label className="cr-check-line">
                <input type="checkbox" name="toIndividual" checked={f.toIndividual} onChange={set('toIndividual')} /> {t('cr.toIndividual')}
              </label>
              {f.toIndividual && (
                <Field label={t('cr.importer')} req hint={t('cr.importerHint')} wide>
                  <input name="importerOfRecord" value={f.importerOfRecord} onChange={set('importerOfRecord')} maxLength={200} list="cr-entities" />
                  <datalist id="cr-entities">{entities.map((e) => <option key={e.key} value={e.name} />)}</datalist>
                </Field>
              )}
            </div>
            {/* Young's rule 6 as ONE box, not a three-way "Shipment to" list (Max, 2026-10-19: it is not in Tina's form):
                a direct shipment says who bears the freight; the incoterm is asked of every request, under the cost. */}
            <div className="cr-row">
              <label className="cr-check-line">
                <input type="checkbox" name="direct" checked={f.shipmentTo !== 'internal'}
                  onChange={(e) => setF((p) => ({ ...p, shipmentTo: e.target.checked ? 'direct' : 'internal' }))} /> {t('cr.toDirect')}
              </label>
              {f.shipmentTo !== 'internal' && (
                <Field label={t('cr.freightPayer')} req hint={t('cr.payerHint')} wide>
                  <input name="freightPayer" value={f.freightPayer} onChange={set('freightPayer')} maxLength={160} />
                </Field>
              )}
            </div>
          </div></section>

          <section className="cr-block cr-b-weight"><div className="cr-hd">{t('cr.b_weight')}</div><div className="cr-bd">
            <div className="cr-row">
              <Field label={t('cr.cartons')} req><input name="cartons" type="number" min="1" step="1" value={f.cartons} onChange={set('cartons')} /></Field>
              <Field label={t('cr.actualKg')} req><input name="actualKg" type="number" min="0" step="0.1" value={f.actualKg} onChange={set('actualKg')} /></Field>
              <Field label={t('cr.dimKg')}><input name="dimKg" type="number" min="0" step="0.1" value={f.dimKg} onChange={set('dimKg')} placeholder={t('cr.dimPh')} /></Field>
              <div className="cr-box">
                <span className="cr-label">{t('cr.boxSize')}</span>
                <div className="cr-box-in">
                  <input aria-label="L" placeholder="L" type="number" value={box.l} onChange={(e) => setBox((b) => ({ ...b, l: e.target.value }))} />×
                  <input aria-label="W" placeholder="W" type="number" value={box.w} onChange={(e) => setBox((b) => ({ ...b, w: e.target.value }))} />×
                  <input aria-label="H" placeholder="H" type="number" value={box.h} onChange={(e) => setBox((b) => ({ ...b, h: e.target.value }))} />
                  <button type="button" className="pa-btn" onClick={fromBox} title={t('cr.useBoxTitle')}>{t('cr.useBox')}</button>
                </div>
              </div>
            </div>
          </div></section>

          <section className="cr-block cr-b-cost"><div className="cr-hd">{t('cr.b_cost')}</div><div className="cr-bd">
            {/* CURRENCY FIRST, then the freight and the surcharges in it (Max, 29/10: "chọn Currency lên đầu"; the
                surcharge is the requester's own figure, in place of a fuel % on the Rates tab) */}
            <div className="cr-row">
              <Field label={t('cr.currency')} req>
                <select name="estCurrency" value={f.estCurrency} onChange={set('estCurrency')}>
                  <option value="">{t('cr.pick')}</option>
                  {(meta.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={t('cr.estCost')} req><MoneyInput name="estCost" value={f.estCost} onChange={set('estCost')} /></Field>
              {/* what it holds is a tooltip, not a line under the box — the row stays one height (Max 29/10) */}
              <Field label={t('cr.estSurcharge')} tip={t('cr.estSurchargeHint')}>
                <MoneyInput name="estSurcharge" value={f.estSurcharge} onChange={set('estSurcharge')} />
              </Field>
              {/* THE SHIPPING TERM, on every request — Tina, 2026-10-19: under DDP Helinox pays the duties and taxes
                  at the destination, so the requester has to estimate them. */}
              <Field label={t('cr.incoterm')} req>
                <select name="incoterm" value={f.incoterm} onChange={set('incoterm')}>
                  <option value="">{t('cr.pick')}</option>
                  {(meta.incoterms || INCOTERMS).map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </Field>
              <Field label={t('cr.duties')} req={f.incoterm === 'DDP'} hint={f.incoterm === 'DDP' ? t('cr.dutiesDdp') : null}>
                <MoneyInput name="estDuties" value={f.estDuties} onChange={set('estDuties')} />
              </Field>
            </div>
            {bulky && (
              <div className="cr-bulkybox" data-testid="cr-bulkybox">
                <p>{t('cr.bulkyNote')}</p>
                <div className="cr-row">
                  <Field label={t('cr.fwdName')} req><input name="fwdName" value={f.fwdName} onChange={set('fwdName')} maxLength={120} /></Field>
                  <Field label={t('cr.fwdCost')} req><MoneyInput name="fwdCost" value={f.fwdCost} onChange={set('fwdCost')} /></Field>
                  <Field label={t('cr.currency')}>
                    <select name="fwdCurrency" value={f.fwdCurrency || f.estCurrency} onChange={set('fwdCurrency')}>
                      <option value="">{t('cr.pick')}</option>
                      {(meta.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            )}
            {/* "The goods" block came off (Max, 2026-10-19: not in Tina's form): Contents and Purpose were nobody's ask,
                Who the goods are for moved to the route; a free note stays, optional. */}
            <Field label={t('cr.notes')} wide><textarea name="notes" rows={2} value={f.notes} onChange={set('notes')} maxLength={1000} /></Field>
          </div></section>
          </fieldset>
        </div>

        <aside className="cr-form-side">
          {view ? <CheckPanel a={r.assessment} remark={r.assessment?.contractKey === 'HPAC-DHL' ? meta.dhlRateRemark : meta.rateRemark} /> : (
            <CheckPanel a={check} busy={checking} remark={check?.contractKey === 'HPAC-DHL' ? meta.dhlRateRemark : meta.rateRemark} missing={[
              !f.carrier && t('cr.need_courier'), !f.destCountry && t('cr.need_dest'), !(Number(f.actualKg) > 0) && t('cr.need_weight'),
              !(Number(f.estCost) > 0) && t('cr.need_cost'), !f.estCurrency && t('cr.need_currency'),
            ].filter(Boolean)} />
          )}
          {errors.length > 0 && (
            <ul className="cr-errors" data-testid="cr-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
          )}
          {view ? <div className="cr-submit">{sideActions}</div> : (
            <>
              <div className="cr-submit">
                <button type="button" className="pa-btn" onClick={onCancel} disabled={busy}>{t('cr.cancel')}</button>
                {/* A request already with the approver is changed by SENDING the change — saving it as a draft would
                    take it out of their queue without a word (Max, 2026-10-19: edit after it was sent). */}
                {!waiting && <button type="button" className="pa-btn" onClick={() => submit(true)} disabled={busy} data-testid="cr-draft">{t('cr.saveDraft')}</button>}
                <button type="button" className="pa-btn submit" onClick={() => submit(false)} disabled={busy} data-testid="cr-send">
                  {busy ? <Loader2 size={14} className="pa-spin" /> : null} {waiting ? t('cr.saveWaiting') : t('cr.send')}
                </button>
              </div>
              {/* Editing one that already waits: just Save or Cancel, no sentence (Max, 2026-10-19: "ko cần nhé chỉ cần
                  save hoặc cancel thui"). Saving still tells the other side what changed — the approver if the requester
                  saved, the requester if the approver did. */}
              {!waiting && <p className="cr-hint">{t('cr.sendNote', { who: (meta.approvers || []).join(', ') || '—' })}</p>}
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

/* ---- a popup ----------------------------------------------------------------------------------- */
/* THE FORM AND A REQUEST OPEN OVER THE LIST (Max, 2026-10-19: "khi bấm new request thì cần hiện là cái popup nhé",
   "click vào phiếu đã tạo thì cũng là popup"). The panel is its own scrollport with no padding, so its head can stick
   without the content scrolling up through padding above it. A request being READ closes on Escape or a click
   outside; the FORM closes only on its own X or Cancel — a stray click must not throw away what was typed.
   z-index 1100: over the header (1000) and the sidebar (950); the decision dialog opened from it is lifted above
   it (overlayClass "over-pad", 1300). */
const CrModal = ({ onClose, guard, children, label }) => {
  useEffect(() => {
    if (guard) return undefined;
    const k = (e) => { if (e.key === 'Escape' && !document.querySelector('.pa-overlay')) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [guard, onClose]);
  return (
    <div className="cr-overlay" role="dialog" aria-modal="true" aria-label={label}
      onMouseDown={(e) => { if (!guard && e.target === e.currentTarget) onClose(); }} data-testid="cr-modal">
      <div className="cr-modal">{children}</div>
    </div>
  );
};

/* ---- what was decided about a request, above its locked form ---------------------------------------- */
const DecisionNote = ({ r, mode }) => {
  const { t } = useTranslation();
  if (r.status === 'approved' || r.status === 'rejected') {
    return (
      <div className={`cr-decision ${r.status}`} data-testid="cr-decision">
        <b>{r.status === 'approved' ? t('cr.approvedBy', { by: r.decidedBy, at: fmtDateTimeVN(r.decidedAt) })
          : t('cr.rejectedBy', { by: r.decidedBy, at: fmtDateTimeVN(r.decidedAt) })}</b>
        {r.decisionNote ? <p>{r.decisionNote}</p> : null}
        {r.status === 'approved' && mode === 'request' ? <p className="cr-hint">{t('cr.rule4')}</p> : null}
      </div>
    );
  }
  if (r.status === 'withdrawn') return <div className="cr-decision withdrawn">{t('cr.withdrawnAt', { at: fmtDateTimeVN(r.withdrawnAt) })}</div>;
  return null;
};

/* ---- the rates the check is measured against ---------------------------------------------- */
const fileToBase64 = (file) => new Promise((ok, bad) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ''));
  r.onerror = bad;
  r.readAsDataURL(file);
});

const Rates = ({ onMsg }) => {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [dir, setDir] = useState('export');
  const [sett, setSett] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const load = useCallback(() => get(`${API}/rates`).then((j) => { setD(j); setSett(j.settings); })
    .catch((e) => onMsg(e.message, true)), [onMsg]);
  useEffect(() => { load(); }, [load]);
  if (!d || !sett) return <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cr.loading')}</div>;

  /* FOUR TABS since 29/10 (Max: "thêm 2 tab mới cho singapore"): HINC's FedEx export / import (KRW) and HPAC's DHL
     export / import (SGD); the banner, the title and the note follow the contract of the tab open. */
  const blk = d[dir] || { lanes: [], rows: [] };
  const dhl = dir.startsWith('sg-');
  const price = new Map(blk.rows.map((r) => [`${r.lane}|${r.from}`, r.amount]));
  const first = blk.lanes[0]?.key;
  const weights = blk.rows.filter((r) => r.lane === first).sort((x, y) => x.from - y.from);
  const v = dhl ? d.versions?.['HPAC-DHL'] : (d.versions?.['HINC-FEDEX'] || d.version);
  const saveSettings = async () => {
    setBusy(true);
    try { await send(`${API}/settings`, 'PUT', sett); onMsg(t('cr.settingsSaved')); await load(); }
    catch (e) { onMsg(e.message, true); } finally { setBusy(false); }
  };
  /* Read first, replace second: the approver sees what the file holds and what it could not place. */
  const pickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const data = await fileToBase64(file);
      const res = await send(`${API}/rates/import`, 'POST', { fileName: file.name, data, dryRun: true });
      setPreview({ fileName: file.name, data, res });
    } catch (err) { onMsg(err.message, true); } finally { setBusy(false); }
  };
  const replace = async () => {
    setBusy(true);
    try {
      await send(`${API}/rates/import`, 'POST', { fileName: preview.fileName, data: preview.data, dryRun: false });
      onMsg(t('cr.ratesLoaded', { file: preview.fileName }));
      setPreview(null);
      await load();
    } catch (err) { onMsg(err.message, true); } finally { setBusy(false); }
  };
  const c = preview?.res?.counts;

  return (
    <>
      <div className={`pa-banner ${v ? 'ok' : 'err'}`} data-testid="cr-rate-version">
        {v ? t('cr.rateVersion', { file: v.fileName, by: v.loadedBy || dash, at: fmtDateTimeVN(v.loadedAt) }) : t('cr.rateNone')}
      </div>

      <div className="pa-section">
        <h3 className="pa-sec-title">{t('cr.settingsTitle')}</h3>
        <p className="pa-sec-note">{t('cr.settingsNote')}</p>
        <div className="cr-row cr-settings">
          {[['priorityFactor', 'cr.priorityFactor', 1, 2, 0.01], ['fuelPct', 'cr.fuelPct', 0, 100, 0.1],
            ['okPct', 'cr.okPct', 0, 100, 0.1], ['reviewPct', 'cr.reviewPct', 0, 100, 0.1]].map(([k, label, min, max, step]) => (
            <Field key={k} label={t(label)}>
              <input type="number" min={min} max={max} step={step} name={k} value={sett[k]} disabled={!d.canEdit}
                onChange={(e) => setSett((p) => ({ ...p, [k]: e.target.value }))} />
            </Field>
          ))}
        </div>
        <div className="cr-row cr-settings">
          <Field label={t('cr.remarkLabel')} wide>
            <textarea name="remark" rows={3} maxLength={1000} value={sett.remark || ''} disabled={!d.canEdit}
              onChange={(e) => setSett((p) => ({ ...p, remark: e.target.value }))} />
          </Field>
        </div>
        {/* HPAC's DHL card prices freight only: its own remark (29/10); the surcharges are typed on each request */}
        <h4 className="cr-subhead">{t('cr.dhlSettingsTitle')}</h4>
        <div className="cr-row cr-settings">
          <Field label={t('cr.dhlRemarkLabel')} wide>
            <textarea name="dhlRemark" rows={3} maxLength={1000} value={sett.dhlRemark || ''} disabled={!d.canEdit}
              onChange={(e) => setSett((p) => ({ ...p, dhlRemark: e.target.value }))} />
          </Field>
          {d.canEdit && <button type="button" className="pa-btn submit" disabled={busy} onClick={saveSettings} data-testid="cr-save-settings">{t('cr.saveSettings')}</button>}
        </div>
      </div>

      <div className="pa-section">
        <div className="cr-form-head">
          <h3 className="pa-sec-title">{t(dhl ? 'cr.ratesTitleDhl' : 'cr.ratesTitle')}</h3>
          {d.canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.pdf" hidden onChange={pickFile} data-testid="cr-rate-file" />
              <button type="button" className="pa-btn" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>{t('cr.upload')}</button>
            </>
          )}
        </div>
        <p className="pa-sec-note">{t(dhl ? 'cr.ratesNoteDhl' : 'cr.ratesNote')}</p>
        {preview && (
          <div className={`cr-preview ${preview.res.errors.length ? 'bad' : ''}`} data-testid="cr-rate-preview">
            <b>{preview.fileName}</b>
            <p>{t('cr.previewCounts', { el: c.exportLanes, ec: c.exportCountries, il: c.importLanes, ic: c.importCountries, rows: c.exportRows + c.importRows })}</p>
            {preview.res.errors.length > 0 && <ul className="cr-plist bad">{preview.res.errors.map((x) => <li key={x}>{x}</li>)}</ul>}
            {preview.res.warnings.length > 0 && <ul className="cr-plist">{preview.res.warnings.map((x) => <li key={x}>{x}</li>)}</ul>}
            <div className="cr-submit">
              <button type="button" className="pa-btn" disabled={busy} onClick={() => setPreview(null)}>{t('cr.cancel')}</button>
              <button type="button" className="pa-btn approve" disabled={busy || preview.res.errors.length > 0} onClick={replace} data-testid="cr-rate-replace">{t('cr.replaceRates')}</button>
            </div>
          </div>
        )}
        {v?.summary?.warnings?.length > 0 && (
          <ul className="cr-plist" data-testid="cr-rate-warnings">{v.summary.warnings.map((x) => <li key={x}>{x}</li>)}</ul>
        )}
        <div className="pa-tabs cr-dirtabs">
          {['export', 'import', 'sg-export', 'sg-import'].map((k) => (
            <button key={k} type="button" className={`pa-tab ${dir === k ? 'active' : ''}`} onClick={() => setDir(k)} data-testid={`cr-dirtab-${k}`}>
              {t(`cr.dir_${k}`)} <span className="pa-count">{(d[k]?.lanes || []).length}</span>
            </button>
          ))}
        </div>
        {!blk.lanes.length ? <div className="pa-empty">{t('cr.rateNone')}</div> : (
          <div className="pa-table-wrap cr-tariff-wrap">
            <table className="pa-table cr-tariff" data-testid={`cr-tariff-${dir}`}>
              <thead>
                <tr>
                  <th className="cr-wcol">{t('cr.weightCol')}</th>
                  {blk.lanes.map((l) => <th key={l.key} className="num" title={l.countries.join(', ') || t('cr.laneNoCountry')}>{l.key}</th>)}
                </tr>
              </thead>
              <tbody>
                {weights.map((w) => (
                  <tr key={w.from}>
                    <td className="cr-wcol mono">{w.kind === 'shipment' ? `${n2(w.from)} kg` : `${n2(w.from)}–${n2(w.to)} kg ${t('cr.perKgShort')}`}</td>
                    {/* SGD prices keep their cents (19.50, not 19.5); KRW has none */}
                    {blk.lanes.map((l) => { const x = price.get(`${l.key}|${w.from}`); return <td key={l.key} className="num">{dhl && x !== undefined ? Number(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n2(x)}</td>; })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <h4 className="cr-subhead">{t('cr.countriesTitle')}</h4>
        <dl className="cr-facts cr-lanes">
          {blk.lanes.map((l) => <div key={l.key}><dt>{l.key}</dt><dd>{l.countries.join(', ') || t('cr.laneNoCountry')}</dd></div>)}
        </dl>
      </div>
    </>
  );
};

/* ---- the screen -------------------------------------------------------------------------------- */
/* THE GUIDE, one per screen (Max, 2026-10-19: "thêm cái guide nhé cho 2 view nhé") — the Sample Tracker's and Cash's
   TourGuide: each step lights a control that is on the page and says what it is for; a step whose control is absent
   (an empty list has no columns) is skipped. It opens by itself once per screen and the Guide button reopens it. */
const TOURS = {
  request: [
    { selector: '[data-testid=cr-new]', title: 'tour.crNewT', body: 'tour.crNewB' },
    { selector: '[data-tour="cr-list"]', title: 'tour.crListT', body: 'tour.crListB' },
    { selector: 'th.cr-h-verdict', title: 'tour.crCheckT', body: 'tour.crCheckB' },
    { selector: 'th.cr-h-status', title: 'tour.crStatusT', body: 'tour.crStatusB' },
    { selector: 'th.cr-h-note', title: 'tour.crNoteT', body: 'tour.crNoteB' },
    { selector: 'th.cr-col-action', title: 'tour.crActT', body: 'tour.crActB' },
  ],
  approval: [
    { selector: '[data-tour="cr-tabs"]', title: 'tour.crqTabsT', body: 'tour.crqTabsB' },
    { selector: '[data-tour="cr-list"]', title: 'tour.crqListT', body: 'tour.crqListB' },
    { selector: 'th.cr-h-verdict', title: 'tour.crCheckT', body: 'tour.crCheckB' },
    { selector: 'th.cr-h-note', title: 'tour.crqNoteT', body: 'tour.crqNoteB' },
    { selector: 'th.cr-col-action', title: 'tour.crqActT', body: 'tour.crqActB' },
    { selector: '[data-tour="cr-rates"]', title: 'tour.crqRatesT', body: 'tour.crqRatesB' },
  ],
};
const TOUR_SEEN = { request: 'portalCourierTourSeen', approval: 'portalCourierApprovalTourSeen' };

/* No "All" tab (Max, 2026-10-19: "bỏ tab all"): every request is in exactly one of the three. */
const TABS = ['waiting', 'approved', 'rejected', 'rates'];

export default function Courier({ mode = 'request' }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const approval = mode === 'approval';
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState(null);
  const [canDecide, setCanDecide] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState(null);   // { mode: 'new' | 'edit' | 'copy', r }
  const [busy, setBusy] = useState(false);
  const [decisionFor, setDecisionFor] = useState(null);
  const [note, setNote] = useState('');
  const tab = approval ? (TABS.includes(params.get('tab')) ? params.get('tab') : 'waiting') : 'mine';
  const openNo = params.get('no') || '';
  const flash = useCallback((text, bad) => setMsg({ text, bad: !!bad }), []);

  const load = useCallback(async () => {
    try {
      const j = await get(`${API}/requests${approval ? '?scope=all' : ''}`);
      setRows(j.requests); setCanDecide(!!j.canDecide);
    } catch (e) { flash(e.message, true); setRows([]); }
  }, [approval, flash]);
  useEffect(() => { get(`${API}/meta`).then(setMeta).catch((e) => flash(e.message, true)); }, [flash]);
  useEffect(() => { load(); }, [load]);

  const inTab = useMemo(() => (rows || []).filter((r) => (
    tab === 'mine' ? true : r.status === tab)), [rows, tab]);
  const count = (k) => (rows || []).filter((r) => r.status === k).length;
  const [openNote, setOpenNote] = useState('');
  const open = (rows || []).find((r) => r.no === openNo) || null;
  const pick = (no) => setParams((p) => { const n = new URLSearchParams(p); if (no) n.set('no', no); else n.delete('no'); return n; });
  const setTab = (k) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', k); n.delete('no'); return n; });
  // Closing a request's popup takes its message with it — it was about that request.
  const closeOpen = useCallback(() => {
    setMsg(null);
    setParams((p) => { const n = new URLSearchParams(p); n.delete('no'); return n; });
  }, [setParams]);

  const columns = [
    { key: 'no', label: t('cr.c_no'), get: (r) => r.no, td: 'mono',
      cell: (r) => <button type="button" className="pa-link" onClick={() => pick(r.no)}>{r.no}</button> },
    /* The requester's list has no Sent column and reads who made a request and when, not who decided it (Max,
       2026-10-19: "bỏ cột sent", "decided by với decide thì đổi thành created by và created at"); the status says where
       it stands. The approver's screen keeps Sent and the decision. */
    ...(approval ? [{ key: 'crSentAt', label: t('cr.c_created'), get: (r) => String(r.submittedAt || '').slice(0, 10), td: 'pa-when',
      cell: (r) => (r.submittedAt ? fmtDateTimeVN(r.submittedAt) : <span className="pa-muted">{t('cr.notSent')}</span>) }] : []),
    ...(approval ? [
      { key: 'by', label: t('cr.c_requester'), get: (r) => r.createdBy || '' },
      { key: 'entity', label: t('cr.c_entity'), get: (r) => r.requesterEntity || '' },
    ] : []),
    /* Route and courier are SIX columns, not two cells holding three facts each (Max, 2026-10-19: "các ra thành các
       cột chứ ko gộp lại nhé"): each has its own funnel, so "everything to Germany" or "every International Economy"
       is one filter rather than a search through joined text. */
    { key: 'originCountry', label: t('cr.c_originCountry'), get: (r) => r.originCountry || '', td: 'cr-w-country' },
    { key: 'originCity', label: t('cr.c_originCity'), get: (r) => r.originCity || '', td: 'cr-w-city' },
    { key: 'destCountry', label: t('cr.c_destCountry'), get: (r) => r.destCountry || '', td: 'cr-w-country' },
    { key: 'destCity', label: t('cr.c_destCity'), get: (r) => r.destCity || '', td: 'cr-w-city' },
    { key: 'carrier', label: t('cr.c_carrier'), get: (r) => carrierOf(r) || '' },
    { key: 'service', label: t('cr.c_service'), get: (r) => r.service || '', td: 'cr-w-service' },
    { key: 'account', label: t('cr.c_account'), get: (r) => r.accountHolder || '' },
    { key: 'kg', label: t('cr.c_kg'), get: (r) => Number(r.chargeableKg) || 0, td: 'num', cell: (r) => n2(r.chargeableKg) },
    { key: 'cost', label: t('cr.c_cost'), get: (r) => r.assessment?.quotedUSD ?? null, td: 'num', cell: (r) => money(r.estCost, r.estCurrency) },
    { key: 'verdict', label: t('cr.c_check'), get: (r) => t(`cr.v_${r.verdict}`), cell: (r) => <VerdictChip v={r.verdict} /> },
    { key: 'status', label: t('cr.c_status'), get: (r) => t(`cr.s_${r.status}`), cell: (r) => <StatusChip s={r.status} /> },
    /* What the approver wrote with the decision (Max, 2026-10-19: "thêm cột manager note nhé"): one line until it is
       clicked, Project Approval's long-remark cell, so a long reason does not take the whole row with it. */
    { key: 'note', label: t('cr.c_managerNote'), get: (r) => r.decisionNote || '', td: 'cr-w-note',
      cell: (r) => (r.decisionNote
        ? <button type="button" className={`pa-note${openNote === r.no ? ' on' : ''}`} title={r.decisionNote}
            aria-expanded={openNote === r.no} onClick={() => setOpenNote((x) => (x === r.no ? '' : r.no))}>{r.decisionNote}</button>
        : dash) },
    ...(approval ? [
      { key: 'decidedBy', label: t('cr.c_decidedBy'), get: (r) => r.decidedBy || '' },
      { key: 'crDecidedAt', label: t('cr.c_decidedAt'), get: (r) => String(r.decidedAt || '').slice(0, 10), td: 'pa-when', cell: (r) => (r.decidedAt ? fmtDateTimeVN(r.decidedAt) : dash) },
    ] : [
      { key: 'createdBy', label: t('cr.c_createdBy'), get: (r) => r.createdBy || '' },
      { key: 'crCreatedAt', label: t('cr.c_createdAt'), get: (r) => String(r.createdAt || '').slice(0, 10), td: 'pa-when', cell: (r) => fmtDateTimeVN(r.createdAt) },
    ]),
  ];
  /* THE ACTION COLUMN (Max, 2026-10-19: "thêm cột action với các tính năng view, edit, submit, duplicate và delete").
     What may be done comes from the server with the row (`can`); a dead button says why on its hover. The approver's
     screen views only — the decision is taken in the request itself. */
  const why = (r, act) => {
    if (approval) return t('cr.why_notWaiting');
    if (!r.mine) return t('cr.why_notMine');
    if (act === 'delete') return t('cr.why_record');
    if (r.status === 'waiting') return t('cr.why_sent');
    if (r.status === 'approved') return act === 'submit' ? t('cr.why_done') : t('cr.why_approved');
    return '';
  };
  /* Each action wears its own colour (Max, 2026-10-19: "3 nút này đang ko có màu") — the same families as the
     portal's buttons: view blue, edit amber, submit navy, duplicate violet, delete red, approve green. */
  const Act = ({ icon: Icon, label, on, ok = true, reason = '', tone = 'view', testid }) => (
    <button type="button" className={`cr-act tone-${tone}`} disabled={!ok || busy}
      title={ok ? label : `${label} — ${reason}`} aria-label={label} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); on(); }}><Icon size={14} /></button>
  );
  columns.push({ key: 'action', label: t('cr.c_action'), get: () => '', noSort: true,
    cell: (r) => (
      <div className="cr-acts">
        <Act icon={Eye} label={t('cr.a_view')} on={() => pick(r.no)} tone="view" testid="cr-a-view" />
        {/* The approver's row: View, Edit a request still waiting, and Approve — the decision dialog opens from the
            row, with Reject inside it (Max, 2026-10-19: "thêm nút edit và approve ở action nhé"). */}
        {approval && <>
          <Act icon={Pencil} label={t('cr.a_edit')} ok={!!r.can?.edit} reason={why(r, 'edit')} on={() => openForm('edit', r)} tone="edit" testid="cr-a-edit" />
          <Act icon={CheckCircle2} label={t('cr.approve')} ok={!!r.can?.decide} reason={why(r, 'decide')}
            on={() => setDecisionFor({ no: r.no, action: 'approve', label: `${r.originCity} → ${r.destCity} · ${carrierOf(r)}` })} tone="approve" testid="cr-a-approve" />
        </>}
        {!approval && <>
          <Act icon={Pencil} label={t('cr.a_edit')} ok={!!r.can?.edit} reason={why(r, 'edit')} on={() => openForm('edit', r)} tone="edit" testid="cr-a-edit" />
          <Act icon={Send} label={t('cr.a_submit')} ok={!!r.can?.submit} reason={why(r, 'submit')} on={() => submitStored(r)} tone="submit" testid="cr-a-submit" />
          <Act icon={Copy} label={t('cr.a_copy')} ok={!!meta?.me?.canRequest} on={() => openForm('copy', r)} tone="copy" testid="cr-a-copy" />
          <Act icon={Trash2} label={t('cr.a_delete')} ok={!!r.can?.delete} reason={why(r, 'delete')} on={() => remove(r)} tone="delete" testid="cr-a-delete" />
        </>}
      </div>
    ) });
  const tools = useColumnTools(inTab, columns.filter((c) => c.key !== 'action'));
  /* THE FIRST FIVE COLUMNS ARE FROZEN (Max, 2026-10-19: "freeze 5 cột đầu lại") — on the requester's list that is the
     request and its route, which say which row is which while the figures scroll by. Offsets MEASURED from the header
     cells (useFrozenColumns): a width on a cell in an auto-layout table is a hint, and "United States - West (AZ, CA,
     …)" takes its column wider than declared. */
  const [tourOpen, setTourOpen] = useState(false);
  const startTour = useCallback(() => {
    setForm(null); closeOpen();
    if (approval && tab === 'rates') setTab('waiting');
    window.setTimeout(() => setTourOpen(true), 250);
  }, [approval, tab, closeOpen]);   // eslint-disable-line react-hooks/exhaustive-deps
  const autoTour = useRef(false);
  useEffect(() => {
    if (rows === null || !meta || autoTour.current) return undefined;
    autoTour.current = true;
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN[approval ? 'approval' : 'request']); } catch { seen = true; }
    if (seen || openNo || form) return undefined;
    const id = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(id);
  }, [rows, meta, approval, openNo, form]);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN[approval ? 'approval' : 'request'], '1'); } catch { /* nothing to remember it in */ }
  };
  const FROZEN = 5;
  const [tableRef, lefts] = useFrozenColumns(FROZEN, [tools.shown.length, columns.length, tab, rows]);
  const frz = (i, base = '') => (i < FROZEN
    ? { className: `${base} cr-frz${i === FROZEN - 1 ? ' cr-frz-last' : ''}`.trim(), style: { left: lefts[i] || 0 } }
    : { className: base });

  const decide = async () => {
    if (!decisionFor) return;
    setBusy(true);
    try {
      await send(`${API}/requests/${encodeURIComponent(decisionFor.no)}/decide`, 'POST', { action: decisionFor.action, note });
      flash(decisionFor.action === 'approve' ? t('cr.didApprove', { no: decisionFor.no }) : t('cr.didReject', { no: decisionFor.no }));
      setDecisionFor(null); setNote('');
      await load();
    } catch (e) {
      /* A refused decision CLOSES the dialog, or its overlay hides the banner saying why. */
      setDecisionFor(null); flash(e.message, true);
    } finally { setBusy(false); }
  };
  const openForm = (m, r = null) => { closeOpen(); setForm({ mode: m, r }); };
  const submitStored = async (r) => {
    setBusy(true);
    try {
      const j = await send(`${API}/requests/${encodeURIComponent(r.no)}/submit`, 'POST');
      await load();
      pick(r.no);
      flash(j.mailed ? t('cr.sentMailed', { no: r.no }) : t('cr.sent', { no: r.no }));
    } catch (e) {
      /* Still missing something: the list of what, and Edit is where it is filled in. */
      flash(e.errors && e.errors.length ? `${r.no}: ${e.errors.join(' · ')}` : e.message, true);
    } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(t(r.status === 'waiting' ? 'cr.deleteAskWaiting' : 'cr.deleteAsk', { no: r.no }))) return;
    setBusy(true);
    try {
      const j = await send(`${API}/requests/${encodeURIComponent(r.no)}`, 'DELETE');
      closeOpen();
      flash(t(j.mailed ? 'cr.didDeleteMailed' : 'cr.didDelete', { no: r.no }));
      await load();
    } catch (e) { flash(e.message, true); } finally { setBusy(false); }
  };
  const withdraw = async (no) => {
    if (!window.confirm(t('cr.withdrawAsk', { no }))) return;
    setBusy(true);
    try { await send(`${API}/requests/${encodeURIComponent(no)}/withdraw`, 'POST'); flash(t('cr.didWithdraw', { no })); await load(); }
    catch (e) { flash(e.message, true); } finally { setBusy(false); }
  };

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main cr-page">
        <ModuleTabs module="courier" />
        {decisionFor && (
          <DecisionDialog
            spec={{ action: decisionFor.action, group: { groupId: decisionFor.no, groupName: decisionFor.label } }}
            note={note} onNote={setNote} busy={busy} selectedIds={[]}
            onSwitch={(action) => setDecisionFor((x) => ({ ...x, action }))}
            onCancel={() => { setDecisionFor(null); setNote(''); }}
            onConfirm={decide}
            overlayClass="over-pad"
          />
        )}
        <div className="pa-title cr-title">
          <div>
            <h2>{approval ? t('cr.approvalTitle') : t('cr.requestTitle')}</h2>
            {/* No sentence under the requester's title (Max, 2026-10-19: "bỏ 2 cái text này"). */}
            {approval && <p>{t('cr.approvalSub', { who: (meta?.approvers || []).join(', ') || dash })}</p>}
          </div>
          <div className="cr-title-acts">
            <button type="button" className="pa-btn cr-guide" title={t('st.tourReopen')} onClick={startTour} disabled={rows === null}
              data-testid="cr-guide"><Compass size={14} /> {t('st.tourButton')}</button>
            {!approval && meta?.me?.canRequest && !form && (
              <button type="button" className="pa-btn submit" onClick={() => openForm('new')} data-testid="cr-new">
                <Plus size={14} /> {t('cr.new')}
              </button>
            )}
          </div>
        </div>
        {/* The message goes where the reader is looking: inside the request popup while one is open (a sent request
            opens straight into it, a decision is taken in it), on the page otherwise. */}
        {msg && !open && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`} data-testid="cr-banner">{msg.text}</div>}

        {form && meta && (
          <CrModal guard label={t('cr.newTitle')} onClose={() => setForm(null)}>
            <RequestForm key={`${form.mode}-${form.r?.no || ''}`} meta={meta} mode={form.mode} r={form.r} onCancel={() => setForm(null)}
              onDone={(r, mailed, draft, byApprover) => {
                setForm(null);
                load().then(() => {
                  pick(r.no);
                  flash(byApprover ? t('cr.changedByYou', { no: r.no }) : draft ? t('cr.savedDraft', { no: r.no })
                    : mailed ? t('cr.sentMailed', { no: r.no }) : t('cr.sent', { no: r.no }));
                });
              }} />
          </CrModal>
        )}

        {approval && (
          <div className="pa-tabs" data-tour="cr-tabs">
            {TABS.map((k) => (
              <button key={k} type="button" className={`pa-tab ${tab === k ? 'active' : ''}`} onClick={() => { setTab(k); tools.clear(); }}
                {...(k === 'rates' ? { 'data-tour': 'cr-rates' } : {})}>
                {t(`cr.tab_${k}`)}{k !== 'rates' ? <span className={`pa-count${k === 'waiting' && count(k) ? ' alert' : ''}`}>{count(k)}</span> : null}
              </button>
            ))}
          </div>
        )}

        {tab === 'rates' ? <Rates onMsg={flash} /> : (
          <>
            {open && meta && (
              <CrModal label={open.no} onClose={closeOpen}>
                {msg && <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`} data-testid="cr-banner">{msg.text}</div>}
                <RequestForm key={`view-${open.no}-${open.status}-${open.submittedAt || ''}`} meta={meta} mode="view" r={open} onCancel={closeOpen}
                  extraTop={<DecisionNote r={open} mode={mode} />}
                  sideActions={approval ? (canDecide && open.status === 'waiting' && <>
                    {open.can?.edit && <button type="button" className="pa-btn" disabled={busy} onClick={() => openForm('edit', open)} data-testid="cr-edit">{t('cr.a_edit')}</button>}
                    <button type="button" className="pa-btn reject" disabled={busy}
                      onClick={() => setDecisionFor({ no: open.no, action: 'reject', label: `${open.originCity} → ${open.destCity} · ${carrierOf(open)}` })}>{t('cr.reject')}</button>
                    <button type="button" className="pa-btn approve" disabled={busy} data-testid="cr-approve"
                      onClick={() => setDecisionFor({ no: open.no, action: 'approve', label: `${open.originCity} → ${open.destCity} · ${carrierOf(open)}` })}>{t('cr.approve')}</button>
                  </>) : <>
                    {open.mine && open.status === 'waiting' && (
                      <button type="button" className="pa-btn" disabled={busy} onClick={() => withdraw(open.no)} data-testid="cr-withdraw">{t('cr.withdraw')}</button>
                    )}
                    {open.can?.delete && <button type="button" className="pa-btn reject" disabled={busy} onClick={() => remove(open)} data-testid="cr-delete">{t('cr.a_delete')}</button>}
                    {meta?.me?.canRequest && <button type="button" className="pa-btn" disabled={busy} onClick={() => openForm('copy', open)} data-testid="cr-copy">{t('cr.a_copy')}</button>}
                    {open.can?.edit && <button type="button" className="pa-btn" disabled={busy} onClick={() => openForm('edit', open)} data-testid="cr-edit">{t('cr.a_edit')}</button>}
                    {open.can?.submit && <button type="button" className="pa-btn submit" disabled={busy} onClick={() => submitStored(open)} data-testid="cr-submit">{t('cr.a_submit')}</button>}
                  </>} />
              </CrModal>
            )}
            <div className="pa-section">
              {/* No "2 of 2" line (Max, 2026-10-19: "bỏ cái này"); the tab carries the count. Clear shows only while a
                  column filter narrows the list. */}
              {tools.active && (
                <div className="pa-toolbar">
                  <button type="button" className="pa-btn" onClick={tools.clear}>{t('cr.clear')}</button>
                </div>
              )}
              {rows === null ? <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cr.loading')}</div>
                : !inTab.length ? <div className="pa-empty">{approval ? t(`cr.empty_${tab}`) : t('cr.emptyMine')}</div> : (
                  <div className="pa-table-wrap" data-tour="cr-list">
                    <table className="pa-table cr-table" data-testid="cr-table" ref={tableRef}>
                      <thead><tr>{columns.map((c, i) => (c.key === 'action'
                        ? <th key="action" className="cr-col-action">{c.label}</th>
                        : <SortTh key={c.key} label={c.label} {...frz(i, `${c.td === 'num' ? 'num' : (c.td || '').startsWith('cr-w-') ? c.td : ''} cr-h-${c.key}`.trim())} {...tools.thProps(c.key)} />))}</tr></thead>
                      <tbody>
                        {tools.shown.map((r) => (
                          <tr key={r.no} className={r.no === openNo ? 'cr-on' : ''} onClick={(e) => { if (!e.target.closest('button')) pick(r.no); }}>
                            {columns.map((c, i) => <td key={c.key} {...(c.key === 'action' ? { className: 'cr-col-action' } : frz(i, c.td || ''))}>{c.cell ? c.cell(r) : (c.get(r) || dash)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </>
        )}
      </div>
      <TourGuide steps={TOURS[approval ? 'approval' : 'request']} open={tourOpen} onClose={closeTour} onFinish={closeTour} />
    </div>
  );
}
